import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, Badge, Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faComment } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const ComentariosMovimientoModal = ({ isOpen, toggle, dataCredito }) => {
  const [comentariosPorAsegurado, setComentariosPorAsegurado] = useState({});
  const [aseguradosAbiertos, setAseguradosAbiertos] = useState({});
  const [movimientosAbiertos, setMovimientosAbiertos] = useState({});

  // Group comments by asegurado (insured)
  useEffect(() => {
    if (dataCredito?.data) {
      const groupedComments = {};
      const reversedData = [...dataCredito.data].reverse();
    
      reversedData.forEach(movimiento => {
        const asegurado = movimiento.asegurado_nombre;
      
        if (!groupedComments[asegurado]) {
          groupedComments[asegurado] = {
            movimientos: [],
            totalComentarios: 0
          };
        }
        const comentariosValidos = movimiento.comentarios?.filter(comentario => 
          comentario && comentario.comentario && comentario.comentario.trim() !== ''
        ) || [];
        
        if (comentariosValidos.length > 0) {
          const comentariosInvertidos = [...comentariosValidos].reverse();
        
          groupedComments[asegurado].movimientos.push({
            id: movimiento.id,
            poliza: movimiento.poliza || 'N/A',
            estado: movimiento.estado_descripcion || 'N/A',
            fechaSolicitud: movimiento.fecha_solicitud || 'N/A',
            tipoSolicitud: movimiento.tipo_solicitud || 'N/A',
            comentarios: comentariosInvertidos,
            montoSolicitado: movimiento.monto_solicitado,
            score: movimiento.score || 'N/A'
          });
          groupedComments[asegurado].totalComentarios += comentariosInvertidos.length;
        }
      });
    
      // Filter out asegurados that have no movimientos with comments
      const filteredGroupedComments = {};
      Object.keys(groupedComments).forEach(asegurado => {
        if (groupedComments[asegurado].movimientos.length > 0) {
          filteredGroupedComments[asegurado] = groupedComments[asegurado];
        }
      });
    
      setComentariosPorAsegurado(filteredGroupedComments);
    
      const initialAseguradosState = {};
      Object.keys(filteredGroupedComments).forEach(asegurado => {
        initialAseguradosState[asegurado] = true;
      });
      setAseguradosAbiertos(initialAseguradosState);
    
      const initialMovimientosState = {};
      Object.keys(filteredGroupedComments).forEach(asegurado => {
        filteredGroupedComments[asegurado].movimientos.forEach(movimiento => {
          initialMovimientosState[`${asegurado}-${movimiento.id}`] = false;
        });
      });
      setMovimientosAbiertos(initialMovimientosState);
    }
  }, [dataCredito]);

  const toggleAsegurado = (asegurado) => {
    setAseguradosAbiertos(prev => ({
      ...prev,
      [asegurado]: !prev[asegurado]
    }));
  };

  const handleAseguradoKeyPress = (event, asegurado) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleAsegurado(asegurado);
    }
  };

  const toggleMovimiento = (asegurado, movimientoId) => {
    const key = `${asegurado}-${movimientoId}`;
    setMovimientosAbiertos(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleMovimientoKeyPress = (event, asegurado, movimientoId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMovimiento(asegurado, movimientoId);
    }
  };

  const toggleAllAsegurados = () => {
    const allOpen = Object.values(aseguradosAbiertos).every(Boolean);
    const newState = {};
    Object.keys(aseguradosAbiertos).forEach(asegurado => {
      newState[asegurado] = !allOpen;
    });
    setAseguradosAbiertos(newState);
  };

  const formatFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';
    try {
      const date = new Date(fecha);
      return date.toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fecha;
    }
  };

  const getEstadoBadgeColor = (estado) => {
    const estadoLower = estado.toLowerCase();

    if ((estadoLower.includes('aprobado')) || estadoLower.includes('aprobado-parcial')) return 'success';

    if (estadoLower.includes('rechazado'))  return 'danger';

    if (estadoLower.includes('pendiente') || estadoLower.includes('en-revision') || estadoLower.includes('procesando')) return 'warning';

    if ((estadoLower.includes('reducida') || estadoLower.includes('bloqueado') || estadoLower.includes('deshabilitada'))) return 'secondary';

    return 'secondary';
  };

  const formatMonto = (monto) => {
    if (!monto || monto === 'N/A') return 'N/A';
    const formattedNumber = new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(monto);

    return `${formattedNumber} UF`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="comentarios-movimiento-modal">
      <ModalHeader toggle={toggle}>
        <div className="modal-header-content w-100">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <h5 className="modal-title mb-0">Comentarios por Movimiento</h5>
              <Badge color="primary" className="ml-2 mt-2">
                {Object.keys(comentariosPorAsegurado).length} Asegurados
              </Badge>
            </div>
            <Button 
              color="link" 
              onClick={toggleAllAsegurados}
              className="ml-2 mt-2 p-0 text-decoration-none"
              size="sm"
            >
              {Object.values(aseguradosAbiertos).every(Boolean) ? 'Contraer todo' : 'Expandir todo'}
            </Button>
          </div>
        </div>
      </ModalHeader>
      
      <ModalBody className="token modal-body">
        {Object.keys(comentariosPorAsegurado).length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No hay comentarios disponibles para los movimientos.</p>
          </div>
        ) : (
          <div className="comentarios-container">
            {Object.entries(comentariosPorAsegurado).map(([asegurado, data]) => (
              <Card key={asegurado} className="asegurado-card mb-3">
                <CardBody className="p-0">
                  {/* Asegurado Header - Clickable */}
                  <div 
                    className="asegurado-header p-3 clickable"
                    onClick={() => toggleAsegurado(asegurado)}
                    onKeyPress={(e) => handleAseguradoKeyPress(e, asegurado)}
                    tabIndex={0}
                    role="button"
                    aria-expanded={aseguradosAbiertos[asegurado]}
                    aria-label={`${aseguradosAbiertos[asegurado] ? 'Contraer' : 'Expandir'} comentarios de ${asegurado}`}
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon 
                          icon={aseguradosAbiertos[asegurado] ? faChevronUp : faChevronDown} 
                          className="mr-2"
                          style={{ width: '16px' }}
                        />
                        <h6 className="asegurado-nombre label mb-0">{asegurado}</h6>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge color="info" pill className="ml-2 mr-2">
                          {data.movimientos.length} movimiento{data.movimientos.length !== 1 ? 's' : ''}
                        </Badge>
                        <Badge color="primary" pill>
                          <FontAwesomeIcon icon={faComment} className="mr-1" />
                          {data.totalComentarios}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Asegurado Content - Collapsible */}
                  <Collapse isOpen={aseguradosAbiertos[asegurado]}>
                    <div className="p-3">
                      <div className="movimientos-list">
                        {data.movimientos.map((movimiento) => {
                          const movimientoKey = `${asegurado}-${movimiento.id}`;
                          return (
                            <Card key={movimiento.id} className="movimiento-card mt-2">
                              <CardBody className="p-0">
                                {/* Movimiento Header - Clickable */}
                                <div 
                                  className="movimiento-header p-3 clickable"
                                  onClick={() => toggleMovimiento(asegurado, movimiento.id)}
                                  onKeyPress={(e) => handleMovimientoKeyPress(e, asegurado, movimiento.id)}
                                  tabIndex={0}
                                  role="button"
                                  aria-expanded={movimientosAbiertos[movimientoKey]}
                                  aria-label={`${movimientosAbiertos[movimientoKey] ? 'Contraer' : 'Expandir'} comentarios del movimiento ${movimiento.id}`}
                                  style={{ cursor: 'pointer', backgroundColor: '#f0f2f5' }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                      <FontAwesomeIcon 
                                        icon={movimientosAbiertos[movimientoKey] ? faChevronUp : faChevronDown} 
                                        className="mr-2"
                                        style={{ width: '14px' }}
                                      />
                                      <span className="movimiento-id label font-weight-bold">
                                        Movimiento #{movimiento.id}
                                      </span>
                                      <Badge 
                                        color={getEstadoBadgeColor(movimiento.estado)} 
                                        className="ml-2 mb-1"
                                      >
                                        {movimiento.estado}
                                      </Badge>
                                    </div>
                                    <Badge color="secondary" pill>
                                      {movimiento.comentarios.length} comentario{movimiento.comentarios.length !== 1 ? 's' : ''}
                                    </Badge>
                                  </div>
                                  <div className="movimiento-metadata mt-2">
                                    <small className="text-muted value">
                                      <strong>Póliza:</strong> {movimiento.poliza} | 
                                      <strong> Score:</strong> {movimiento.score} | 
                                      <strong> Monto:</strong> {formatMonto(movimiento.montoSolicitado)} |
                                      <strong> Fecha:</strong> {formatFecha(movimiento.fechaSolicitud)}
                                    </small>
                                  </div>
                                </div>

                                {/* Movimiento Content - Collapsible */}
                                <Collapse isOpen={movimientosAbiertos[movimientoKey]}>
                                  <div className="p-3">
                                    <div className="comentarios-list">
                                      {movimiento.comentarios.map((comentario, index) => (
                                        <div key={comentario.id || index} className="comentario-item mb-3 p-3">
                                          <div className="comentario-header d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                              <strong className="label d-block">
                                                {comentario.nombre_author || 'Usuario'}
                                              </strong>
                                              <small className="text-muted value">
                                                {comentario.fecha_creacion ? formatFecha(comentario.fecha_creacion) : 'Fecha no disponible'}
                                              </small>
                                            </div>
                                          </div>
                                          <div className="comentario-content value p-2 bg-light rounded">
                                            {comentario.comentario}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </Collapse>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </Collapse>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </ModalBody>
      

    </Modal>
  );
};

ComentariosMovimientoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  dataCredito: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        asegurado_nombre: PropTypes.string,
        estado_descripcion: PropTypes.string,
        poliza: PropTypes.string,
        fecha_solicitud: PropTypes.string,
        tipo_solicitud: PropTypes.string,
        monto_solicitado: PropTypes.number,
        score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        comentarios: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            nombre_author: PropTypes.string,
            comentario: PropTypes.string,
            fecha_creacion: PropTypes.string
          })
        )
      })
    )
  }),
};

ComentariosMovimientoModal.defaultProps = {
  dataCredito: { data: [] },
};

export default ComentariosMovimientoModal;